import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon, ShoppingCartIcon, MapPinIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import Money from '../components/Money';
import EmptyState from '../components/EmptyState';
import { browseListings } from '../api/marketplaceApi';
import type { Listing } from '../types';

const CATEGORIES = ['All', 'Fruits', 'Vegetables', 'Grains', 'Legumes', 'Spices', 'Dairy', 'Livestock'];
const UNITS = ['All', 'kg', 'tonne', 'box', 'crate', 'bag', 'piece', 'litre'];

const CatalogueBrowser: React.FC = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState<Listing[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [unit, setUnit] = useState('All');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('created_at');
  const [cartItems, setCartItems] = useState<{ listing: Listing; quantity: number }[]>([]);

  const loadListings = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 24, sort: sortBy, order: 'DESC' };
      if (search) params.search = search;
      if (category !== 'All') params.category = category;
      if (unit !== 'All') params.unit = unit;
      const result = await browseListings(params);
      setListings(result.items || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('Failed to load listings:', err);
    } finally {
      setLoading(false);
    }
  }, [page, category, unit, search, sortBy]);

  useEffect(() => { loadListings(); }, [loadListings]);

  const addToCart = (listing: Listing) => {
    const minQty = Number(listing.minimum_order_quantity) || 1;
    setCartItems(prev => {
      const existing = prev.find(i => i.listing.id === listing.id);
      if (existing) {
        return prev.map(i => i.listing.id === listing.id ? { ...i, quantity: i.quantity + minQty } : i);
      }
      return [...prev, { listing, quantity: minQty }];
    });
  };

  const totalItems = cartItems.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Marketplace Catalogue</h2>
          <p className="text-sm text-gray-500 mt-0.5">Browse verified supplier listings</p>
        </div>
        {cartItems.length > 0 && (
          <button
            onClick={() => navigate('/marketplace/buyer/orders/new', { state: { cartItems } })}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700"
          >
            <ShoppingCartIcon className="w-4 h-4" />
            Create Order ({totalItems} items)
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex flex-wrap gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search products, suppliers, grades..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Category */}
          <select
            value={category}
            onChange={e => { setCategory(e.target.value); setPage(1); }}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
          >
            {CATEGORIES.map(c => <option key={c} value={c}>{c === 'All' ? 'All Categories' : c}</option>)}
          </select>

          {/* Unit */}
          <select
            value={unit}
            onChange={e => { setUnit(e.target.value); setPage(1); }}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
          >
            {UNITS.map(u => <option key={u} value={u}>{u === 'All' ? 'All Units' : u}</option>)}
          </select>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
          >
            <option value="created_at">Newest First</option>
            <option value="price_per_unit">Price: Low to High</option>
            <option value="available_quantity">Most Available</option>
          </select>
        </div>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{total} listing{total !== 1 ? 's' : ''} found</p>
      </div>

      {/* Listing Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse">
              <div className="h-4 bg-gray-100 rounded w-3/4 mb-3" />
              <div className="h-3 bg-gray-100 rounded w-1/2 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-2/3 mb-4" />
              <div className="h-8 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      ) : listings.length === 0 ? (
        <EmptyState
          title="No listings found"
          description="Try adjusting your filters or search terms."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {listings.map(listing => (
            <div key={listing.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
              {/* Product image placeholder */}
              <div className="h-32 bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center">
                {listing.image_url ? (
                  <img src={listing.image_url} alt={listing.title} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-emerald-300 text-4xl">🌿</span>
                )}
              </div>

              <div className="p-4">
                {/* Title and supplier */}
                <h3 className="text-sm font-semibold text-gray-900 truncate">{listing.title}</h3>
                <div className="flex items-center gap-1 mt-1">
                  {listing.supplier_verified === 'verified' && (
                    <CheckBadgeIcon className="w-3.5 h-3.5 text-emerald-500" />
                  )}
                  <span className="text-xs text-gray-500 truncate">{listing.supplier_name || 'Verified Supplier'}</span>
                </div>

                {/* Grade and certification */}
                <div className="flex items-center gap-2 mt-2">
                  {listing.grade && (
                    <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                      Grade {listing.grade}
                    </span>
                  )}
                  {listing.traceability_enabled && (
                    <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-xs rounded">
                      Traceable
                    </span>
                  )}
                </div>

                {/* Price and availability */}
                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <p className="text-lg font-bold text-gray-900">
                      <Money amount={listing.price_per_unit} currency={listing.currency} />
                    </p>
                    <p className="text-xs text-gray-400">per {listing.commercial_unit}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Available</p>
                    <p className="text-sm font-semibold text-emerald-600">
                      {Number(listing.available_quantity) - Number(listing.reservation_quantity || 0)} {listing.commercial_unit}
                    </p>
                  </div>
                </div>

                {/* MOQ */}
                {Number(listing.minimum_order_quantity) > 1 && (
                  <p className="text-xs text-gray-400 mt-1">Min. order: {listing.minimum_order_quantity} {listing.commercial_unit}</p>
                )}

                {/* Location */}
                {listing.fulfillment_city && (
                  <div className="flex items-center gap-1 mt-2">
                    <MapPinIcon className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-400">{listing.fulfillment_city}</span>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => navigate(`/marketplace/buyer/catalogue?view=${listing.id}`)}
                    className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => addToCart(listing)}
                    className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
                  >
                    Add to Order
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 24 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm font-medium border rounded-lg disabled:opacity-40"
          >
            Previous
          </button>
          <span className="px-3 py-1.5 text-sm text-gray-500">Page {page} of {Math.ceil(total / 24)}</span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={page >= Math.ceil(total / 24)}
            className="px-3 py-1.5 text-sm font-medium border rounded-lg disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default CatalogueBrowser;
